import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { ICellRendererParams } from 'ag-grid-community';
import { Button, InputGroup } from '@blueprintjs/core';
import { TranslatedMessage } from '../common/translation';
import { UserStore } from '../../store/UserStore';
import { GroupStore } from '../../store/GroupStore';
import { LocaleStore } from '../../store';
import { ModalStore } from '../../store/ModalStore';
import { Link } from 'react-router-dom';
import { Page, TLCode } from '../../common/enums';
import { Instance } from 'mobx-state-tree';
import { ApplicationStore } from '../../store';
import styled from '@emotion/styled';
import BaseGrid from '../common/grid/BaseGrid';
import MainSectionLayout from '../layout/MainSectionLayout';
import EditCell from '../common/grid/EditCell';

interface GroupListPageProps {
  UserStore?: Instance<typeof UserStore>;
  GroupStore?: Instance<typeof GroupStore>;
  LocaleStore?: Instance<typeof LocaleStore>;
  ModalStore?: Instance<typeof ModalStore>;
  ApplicationStore?: Instance<typeof ApplicationStore>;
}

interface GroupListPageState {
  isLoading: boolean;
  filter: string;
  columnDefs: any;
  defaultColDef: any;
  api?: any;
  columnApi?: any;
  // users?: Array<Instance<typeof User>>;
}

// const Search = Input.Search;
export function renderStatus(params: ICellRendererParams) {
  const { value } = params;
  return value ? '' : 'no';
}

@inject('UserStore', 'GroupStore', 'LocaleStore', 'ModalStore', 'ApplicationStore')
@observer
export default class GroupListPage extends React.Component<GroupListPageProps, GroupListPageState> {
  deleteUser = async (id: number) => {
    const confirm = await this.props.ModalStore!.confirm.show('title', 'Are you sure you want to delete user?');
    if (confirm) {
      await this.props.UserStore!.deleteUsers([id]);
    }
  };

  constructor(props: {}) {
    super(props);
    this.state = {
      isLoading: false,
      filter: '',
      defaultColDef: {
        filter: false,
        sortable: true,
      },
      columnDefs: [
        {
          headerName: this.props.LocaleStore!.getText(TLCode.GROUP_Name),
          // width: 200,
          sort: 'asc',
          field: 'name',
        },
        {
          headerName: this.props.LocaleStore!.getText(TLCode.GROUP_Description),
          width: 200,
          field: 'description',
          sortingOrder: ['asc', 'desc'],
        },
        {
          headerName: this.props.LocaleStore!.getText(TLCode.GROUP_MemberCount),
          width: 100,
          field: 'memberCount',
          sortingOrder: ['asc', 'desc'],
        },
        {
          headerName: this.props.LocaleStore!.getText(TLCode.GROUP_Color),
          width: 100,
          field: 'color',
        },
        {
          headerName: this.props.LocaleStore!.getText(TLCode.GROUP_TargetDutyCount),
          width: 100,
          field: 'targetDutyCount',
        },
        {
          headerName: this.props.LocaleStore!.getText(TLCode.GROUP_TargetDayDutyCount),
          width: 100,
          field: 'targetDayDutyCount',
        },
        {
          headerName: this.props.LocaleStore!.getText(TLCode.GROUP_TargetNightDutyCount),
          width: 100,
          field: 'targetNightDutyCount',
        },
        {
          headerName: '',
          width: 100,
          suppressSorting: true,
          filter: false,
          cellRendererFramework: EditCell,
          cellRendererParams: (params: ICellRendererParams) => {
            return {
              onEditButtonClick: () => {
                this.props.ApplicationStore!.navigate(`${Page.GroupPage}/${params.data.id}`);
              },
            };
          },
        },
      ],
    };
  }

  _isMounted = false;

  async componentDidMount() {
    this._isMounted = true;
    const { GroupStore, UserStore } = this.props;
    this._isMounted && this.setState({ isLoading: true });
    // await UserStore!.getWithRole();
    await GroupStore!.checkListLoaded(); //loadList();
    this._isMounted && this.setState({ isLoading: false });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  buildGridData = () => {
    return this.props.GroupStore!.dataList.map((group) => {
      return {
        id: group.groupId,
        name: group.name,
        description: group.description,
        memberCount: group.memberCount,
        color: group.color,
        targetDutyCount: group.targetDutyCount,
        targetDayDutyCount: group.targetDayDutyCount,
        targetNightDutyCount: group.targetNightDutyCount,
      };
    });
  };

  setFilterText(filter) {
    this.setState({
      filter,
    });
  }

  private onRowDoubleClicked = (ev) => {
    this.props.ApplicationStore!.navigate(`${Page.GroupPage}/${ev.data.id}`);
  };

  public render() {
    return (
      <MainSectionLayout isLoading={this.state.isLoading} title={TLCode.GROUPS_Title}>
        <ContainerCol>
          <DivLine>
            <DivLeft>
              <InputGroup
                placeholder="Filter"
                leftIcon="search"
                value={this.state.filter}
                onChange={(e) => this.setFilterText(e.target.value)}
                onKeyPress={(e) => {
                  if (e.charCode == 13) {
                    this.props.ApplicationStore!.navigate(
                      `${Page.GroupPage}/${
                        this.props.GroupStore!.dataList[
                          this.props.GroupStore!.dataList.findIndex((grp) => grp.name === this.state.filter)
                        ].groupId
                      }`,
                    );
                  }
                  return false;
                }}
              />
            </DivLeft>
            <DivRight>
              <Link to={Page.GroupPage + '/-1'}>
                <Button intent="primary">
                  <TranslatedMessage id={TLCode.Add} />
                </Button>
              </Link>
            </DivRight>
          </DivLine>
          <BaseGrid
            keyName="id"
            defaultColDef={this.state.defaultColDef}
            columnDefs={this.state.columnDefs}
            rowData={this.buildGridData()}
            loading={this.state.isLoading}
            quickFilterText={this.state.filter}
            onRowDoubleClicked={this.onRowDoubleClicked}
          />
        </ContainerCol>
      </MainSectionLayout>
    );
  }
}

const ContainerCol = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;
const DivLeft = styled.div`
  display: flex;
  flex-direction: column;
  width: 30%;
  margin-right: 5%;
  padding-bottom: 5px;
`;
const DivRight = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 5px;
  .bp3-icon {
    margin-right: 3px;
  }
`;
